import axios from "axios";
import { API_URL } from "@/config/api";
import { knownShioriDestinationImage } from "@/utils/candidate-presentation.js";
import {
  getSessionSnapshot,
  isCurrentSessionSnapshot,
} from "@/api/session-context.js";

const inFlightImages = new Map();
const IMAGE_TIMEOUT_MS = 10000;

const abortError = () => new DOMException("Image workflow aborted", "AbortError");

function validImageBlob(value) {
  return value instanceof Blob && value.size > 0 && value.type.startsWith("image/");
}

async function runAbortable(task, timeoutMs, workflowSignal) {
  if (workflowSignal.aborted) throw abortError();
  const controller = new AbortController();
  const abort = () => controller.abort();
  workflowSignal.addEventListener("abort", abort, { once: true });
  let timer;
  try {
    return await Promise.race([
      task(controller.signal),
      new Promise((_resolve, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error("image request timed out"));
        }, timeoutMs);
      }),
      new Promise((_resolve, reject) => {
        workflowSignal.addEventListener("abort", () => reject(abortError()), {
          once: true,
        });
      }),
    ]);
  } finally {
    clearTimeout(timer);
    workflowSignal.removeEventListener("abort", abort);
  }
}

async function resolveImageBlob(source, timeoutMs, workflowSignal) {
  if (!source) return null;
  const resolved =
    typeof source === "function"
      ? await runAbortable(
          (signal) => source({ signal, timeout: timeoutMs }),
          timeoutMs,
          workflowSignal,
        )
      : source;
  if (validImageBlob(resolved)) return resolved;
  if (
    typeof resolved !== "string" ||
    !resolved ||
    resolved.includes("placeholder")
  ) {
    return null;
  }
  return runAbortable(async (signal) => {
    const response = await fetch(resolved, { signal });
    if (!response.ok) return null;
    const blob = await response.blob();
    return validImageBlob(blob) ? blob : null;
  }, timeoutMs, workflowSignal);
}

function imageExtension(type) {
  if (type === "image/webp") return "webp";
  if (type === "image/png") return "png";
  if (type === "image/gif") return "gif";
  return "jpg";
}

export const beginShioriDestinationImageWorkflow = () => getSessionSnapshot();

export const isShioriDestinationImageWorkflowCurrent = (session) =>
  isCurrentSessionSnapshot(session);

export function invalidateShioriDestinationImageWorkflows() {
  for (const workflow of inFlightImages.values()) workflow.controller.abort();
  inFlightImages.clear();
}

async function ensureImage(
  {
    shioriId,
    destinationName,
    explicitCandidateImage,
    shioriStore,
    timeoutMs = IMAGE_TIMEOUT_MS,
  },
  workflow,
) {
  const isCurrent = () =>
    !workflow.controller.signal.aborted &&
    isShioriDestinationImageWorkflowCurrent(workflow.session);
  if (!shioriId || !shioriStore?.uploadShioriImage || !isCurrent()) return false;

  try {
    await axios.get(`${API_URL}/shiori/${shioriId}/image`, {
      responseType: "blob",
      timeout: timeoutMs,
      signal: workflow.controller.signal,
    });
    return false;
  } catch (error) {
    if (!isCurrent() || error?.response?.status !== 404) return false;
  }

  let blob = null;
  try {
    blob = await resolveImageBlob(
      explicitCandidateImage,
      timeoutMs,
      workflow.controller.signal,
    );
  } catch (_error) {
    if (!isCurrent()) return false;
  }
  if (!isCurrent()) return false;

  if (!blob) {
    const fallback = knownShioriDestinationImage(destinationName);
    if (!fallback) return false;
    try {
      blob = await resolveImageBlob(
        fallback,
        timeoutMs,
        workflow.controller.signal,
      );
    } catch (_error) {
      return false;
    }
  }

  if (!isCurrent() || !validImageBlob(blob)) return false;
  const file = new File(
    [blob],
    `destination.${imageExtension(blob.type)}`,
    { type: blob.type },
  );
  try {
    await shioriStore.uploadShioriImage(shioriId, file, {
      onlyIfEmpty: true,
      refresh: false,
      timeout: timeoutMs,
      signal: workflow.controller.signal,
      expectedSession: workflow.session,
    });
    return isCurrent();
  } catch (_error) {
    return false;
  }
}

export function ensureShioriDestinationImage(options) {
  const session = options?.session || beginShioriDestinationImageWorkflow();
  if (!isShioriDestinationImageWorkflowCurrent(session)) {
    return Promise.resolve(false);
  }
  const shioriId = String(options?.shioriId || "");
  if (!shioriId) return Promise.resolve(false);
  const key = `${session.generation}:${shioriId}`;
  if (inFlightImages.has(key)) return inFlightImages.get(key).promise;

  const workflow = { session, controller: new AbortController() };
  const request = ensureImage(options, workflow).finally(() => {
    if (inFlightImages.get(key)?.promise === request) inFlightImages.delete(key);
  });
  inFlightImages.set(key, { ...workflow, promise: request });
  return request;
}
